import {
    enablePatches,
    produceWithPatches,
    applyPatches,
    setAutoFreeze,
} from "immer";
import {
    eventGroupState,
    setEventGroupState,
} from "@app/store/eventGroupStore.svelte.js";
import { metadataState, setMetadataState } from "@app/store/metadataStore.svelte.js";
import {
    projectState,
    resetProjectState,
} from "@app/store/projectStore.svelte.js";
import { speakerState, setSpeakerState } from "@app/store/speakerStore.svelte.js";
import { issueState, setIssues } from "@app/store/issueStore.svelte.js";
import { backfillAudioRenderHashes } from "@app/lib/audio/audioRenderState.js";
import {
    markerState,
    resetMarkerStateWithData,
} from "@app/store/markerStore.svelte.js";
import { fontState, updateFontState } from "@app/store/fontStore.svelte.js";
import { styleState, setStyleState } from "@app/store/styleStore.svelte.js";
import { diffPatches } from "@app/lib/historyDiff.js";

enablePatches();
// Svelte $state mutates store objects in place; Immer's frozen trees break assigns/bindings.
setAutoFreeze(false);

/*
 * History model
 * -------------
 * actions[0] is a baseline marker; every later action holds forward `patches`
 * and `inversePatches` per store, relative to the action before it.
 *
 * `committed` is a plain-JSON shadow of every tracked store as of the current
 * history position. New entries are diffed against it, so a snapshot that
 * covers only one store (the common legacy call) still produces an exact,
 * reversible step no matter what kind of entry preceded it.
 */

/** Undoable steps kept after the baseline. Entries are diffs, so this is cheap. */
const MAX_UNDO_ENTRIES = 100;

/** @typedef {'eventGroupState'|'metadataState'|'projectState'|'speakerState'|'issueState'|'markerState'|'fontState'|'styleState'} HistoryStoreName */

/** @type {HistoryStoreName[]} */
const TRACKED_STORES = [
    "eventGroupState",
    "metadataState",
    "projectState",
    "speakerState",
    "issueState",
    "markerState",
    "fontState",
    "styleState",
];

export const historyState = $state({
    actions: [],
    position: 0,
    /** @type {number | null} */
    savedPosition: null,
});

/** @type {Partial<Record<HistoryStoreName, unknown>>} */
let committed = {};

/** @type {Set<() => void>} */
const pendingFlushers = new Set();

/**
 * Selection is view state, not document content: an entry that only moved the
 * selection would make Undo look like it did nothing.
 * @param {HistoryStoreName} storeName
 */
function ignoreFor(storeName) {
    if (storeName !== "eventGroupState") {
        return undefined;
    }
    return (path) => path.length === 2 && path[1] === "selected";
}

export function hasUnsavedChanges() {
    if (historyState.savedPosition == null) {
        return historyState.actions.length > 0;
    }
    return historyState.position !== historyState.savedPosition;
}

export function markHistorySaved() {
    historyState.savedPosition = historyState.position;
}

/**
 * Register a callback that commits edits still waiting on a debounce (e.g.
 * Quill typing) so Undo/Redo never runs ahead of them.
 * @param {() => void} flush
 * @returns {() => void} unregister
 */
export function registerHistoryFlush(flush) {
    pendingFlushers.add(flush);
    return () => pendingFlushers.delete(flush);
}

export function flushPendingHistory() {
    for (const flush of [...pendingFlushers]) {
        try {
            flush();
        } catch (err) {
            console.warn("[history] pending flush failed", err);
        }
    }
}

function plainClone(value) {
    return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

/**
 * @param {HistoryStoreName} storeName
 */
export function getStorePlain(storeName) {
    switch (storeName) {
        case "eventGroupState":
            return plainClone(eventGroupState);
        case "metadataState":
            return plainClone(metadataState);
        case "projectState":
            return plainClone(projectState);
        case "speakerState":
            return plainClone(speakerState);
        case "issueState":
            return plainClone(issueState.value);
        case "markerState":
            return plainClone(markerState);
        case "fontState":
            return plainClone(fontState);
        case "styleState":
            return plainClone(styleState);
        default:
            throw new Error(`Unknown history store: ${storeName}`);
    }
}

/**
 * @param {HistoryStoreName} storeName
 * @param {unknown} value
 */
export function setStorePlain(storeName, value) {
    switch (storeName) {
        case "eventGroupState":
            setEventGroupState(value);
            break;
        case "metadataState":
            setMetadataState(value);
            break;
        case "projectState":
            resetProjectState(value);
            break;
        case "speakerState":
            setSpeakerState(value);
            break;
        case "issueState":
            setIssues(value);
            break;
        case "markerState":
            resetMarkerStateWithData(value);
            break;
        case "fontState":
            updateFontState(value);
            break;
        case "styleState":
            setStyleState(value);
            break;
        default:
            throw new Error(`Unknown history store: ${storeName}`);
    }
}

function syncCommittedFromLive() {
    committed = {};
    for (const storeName of TRACKED_STORES) {
        committed[storeName] = getStorePlain(storeName);
    }
}

/**
 * Diff `next` against the committed shadow and advance the shadow.
 * @param {HistoryStoreName} storeName
 * @param {unknown} next plain value (not shared with any live store)
 */
function diffAgainstCommitted(storeName, next) {
    const prev = committed[storeName];
    committed[storeName] = next;
    if (prev === undefined) {
        // No known prior state: nothing to undo to for this store.
        return null;
    }
    const ignore = ignoreFor(storeName);
    const patches = diffPatches(prev, next, { ignore });
    if (patches.length === 0) {
        return null;
    }
    return { patches, inversePatches: diffPatches(next, prev, { ignore }) };
}

/**
 * Apply a per-store patch map to the live stores and the committed shadow.
 * @param {Record<string, any[]>} patchesByStore
 */
function applyPatchesMap(patchesByStore) {
    if (!patchesByStore) {
        return;
    }
    for (const [storeName, patches] of Object.entries(patchesByStore)) {
        if (!patches?.length) {
            continue;
        }
        const name = /** @type {HistoryStoreName} */ (storeName);
        let next;
        try {
            // Apply to the live value so view-only fields (selection, etc.) survive.
            next = applyPatches(getStorePlain(name), patches);
        } catch (err) {
            console.warn(
                `[history] patch did not apply to live ${name}; using committed state`,
                err,
            );
            next = applyPatches(committed[name], patches);
        }
        setStorePlain(name, plainClone(next));
        // Setters may normalize (e.g. audio render hashes); track what actually landed.
        committed[name] = getStorePlain(name);
    }
}

function clampSelection(groupIndex) {
    const group = eventGroupState[groupIndex];
    if (!group || !Array.isArray(group.selected)) {
        return;
    }
    const count = group.events?.length ?? 0;
    const valid = group.selected.filter((i) => i < count);
    if (valid.length !== group.selected.length) {
        group.selected = valid;
    }
}

/** Show the event group the undone/redone change belongs to. */
function focusActionGroup(action) {
    const group = action?.eventGroup;
    if (group !== false && group !== undefined && group !== null && eventGroupState[group]) {
        projectState.selected = group;
    }
    if (projectState.selected !== false && projectState.selected !== undefined) {
        clampSelection(projectState.selected);
    }
}

/**
 * @param {object} action
 * @param {Record<string, any[]>} patches
 * @param {Record<string, any[]>} inversePatches
 * @param {{ coalesceMs?: number, coalesceKey?: string|number }} options
 * @returns {boolean} whether an entry was recorded
 */
function pushAction(action, patches, inversePatches, options = {}) {
    if (Object.keys(patches).length === 0) {
        // Nothing changed: recording this would make the next Undo a no-op.
        return false;
    }

    /* Remove any redo actions from the history stack */
    if (historyState.position < historyState.actions.length) {
        historyState.actions = historyState.actions.slice(
            0,
            historyState.position,
        );
        if (
            historyState.savedPosition != null &&
            historyState.savedPosition > historyState.position
        ) {
            historyState.savedPosition = null;
        }
    }

    const now = Date.now();
    const tip = historyState.actions[historyState.actions.length - 1];
    if (
        options.coalesceMs &&
        options.coalesceKey !== undefined &&
        historyState.actions.length > 1 &&
        tip?.kind === "patches" &&
        tip.name === action.name &&
        tip.coalesceKey === options.coalesceKey &&
        tip.eventGroup === action.eventGroup &&
        now - (tip.time ?? 0) <= options.coalesceMs &&
        historyState.savedPosition !== historyState.position
    ) {
        // Fold rapid repeats (e.g. typing bursts) into a single undo step.
        const merged = { ...tip, time: now, patches: { ...tip.patches }, inversePatches: { ...tip.inversePatches } };
        for (const [store, list] of Object.entries(patches)) {
            merged.patches[store] = [...(tip.patches[store] ?? []), ...list];
        }
        for (const [store, list] of Object.entries(inversePatches)) {
            merged.inversePatches[store] = [...list, ...(tip.inversePatches[store] ?? [])];
        }
        historyState.actions[historyState.actions.length - 1] = merged;
        historyState.position = historyState.actions.length;
        return true;
    }

    const entry = {
        name: action.name,
        eventGroup:
            action.eventGroup !== undefined ? action.eventGroup : projectState.selected,
        kind: "patches",
        time: now,
        coalesceKey: options.coalesceKey,
        patches,
        inversePatches,
    };

    let nextActions = [...historyState.actions, entry];
    const overflow = nextActions.length - 1 - MAX_UNDO_ENTRIES;
    if (overflow > 0) {
        nextActions = [nextActions[0], ...nextActions.slice(1 + overflow)];
        if (historyState.savedPosition != null) {
            if (historyState.savedPosition > 1 + overflow) {
                historyState.savedPosition -= overflow;
            } else if (historyState.savedPosition > 1) {
                historyState.savedPosition = null;
            }
        }
    }

    historyState.actions = nextActions;
    historyState.position = historyState.actions.length;
    return true;
}

function startHistory(name, eventGroup) {
    historyState.actions = [
        {
            name,
            eventGroup:
                eventGroup !== undefined ? eventGroup : projectState.selected,
            kind: "baseline",
        },
    ];
    historyState.position = 1;
}

/**
 * Record an action whose store mutations have already been applied.
 * `snapshots` lists the post-change value of each store that changed; every
 * other store is left alone.
 * @param {{ name: string, eventGroup?: number|false, snapshots?: Array<{store: string, value: unknown}> }} action
 * @param {{ coalesceMs?: number, coalesceKey?: string|number }} [options]
 * @returns {boolean} whether an entry was recorded
 */
export function insertHistoryAction(action, options = {}) {
    if (historyState.actions.length === 0) {
        // No baseline yet (project not loaded through a known path): adopt the
        // current state as the baseline instead of an undoable step.
        startHistory(action.name, action.eventGroup);
        syncCommittedFromLive();
        return false;
    }

    /** @type {Record<string, any[]>} */
    const patches = {};
    /** @type {Record<string, any[]>} */
    const inversePatches = {};

    for (const snapshot of action.snapshots ?? []) {
        const storeName = /** @type {HistoryStoreName} */ (snapshot.store);
        if (!TRACKED_STORES.includes(storeName)) {
            console.warn(`[history] unknown store "${snapshot.store}" ignored`);
            continue;
        }
        const next =
            typeof snapshot.value === "string"
                ? JSON.parse(snapshot.value)
                : plainClone(snapshot.value);
        const diff = diffAgainstCommitted(storeName, next);
        if (diff) {
            patches[storeName] = diff.patches;
            inversePatches[storeName] = diff.inversePatches;
        }
    }

    return pushAction(action, patches, inversePatches, options);
}

export function resetHistory() {
    historyState.actions = [];
    historyState.position = 0;
    historyState.savedPosition = null;
    committed = {};
}

/**
 * Snapshot project document stores.
 * @returns {Array<{store: string, value: string}>}
 */
export function buildDocumentSnapshots() {
    return [
        { store: "eventGroupState", value: JSON.stringify(eventGroupState) },
        { store: "metadataState", value: JSON.stringify(metadataState) },
        { store: "projectState", value: JSON.stringify(projectState) },
        { store: "speakerState", value: JSON.stringify(speakerState) },
        { store: "issueState", value: JSON.stringify(issueState.value) },
        { store: "markerState", value: JSON.stringify(markerState) },
    ];
}

/**
 * Wipe history and insert a clean baseline (load/create/import/sync).
 *
 * "save project" keeps the undo stack: it only marks the current position as
 * saved, so users can still undo past a save.
 * @param {{ name?: string, eventGroup?: number|false, snapshots?: Array<{store: string, value: unknown}> }} [options]
 */
export function establishHistoryBaseline({
    name = "load project",
    eventGroup,
    snapshots,
} = {}) {
    if (name === "save project" && historyState.actions.length > 0) {
        flushPendingHistory();
        // Saving may stamp fields like localPath/name; don't attribute those
        // to the user's next edit.
        syncCommittedFromLive();
        markHistorySaved();
        return;
    }

    if (name !== "save project") {
        backfillAudioRenderHashes(eventGroupState);
    }
    resetHistory();
    startHistory(name, eventGroup);
    syncCommittedFromLive();
    for (const snapshot of snapshots ?? []) {
        if (TRACKED_STORES.includes(snapshot.store)) {
            committed[snapshot.store] =
                typeof snapshot.value === "string"
                    ? JSON.parse(snapshot.value)
                    : plainClone(snapshot.value);
        }
    }
    markHistorySaved();
}

/**
 * Commit store mutations via an Immer recipe (preferred for new edit paths).
 * @param {{
 *   name: string,
 *   eventGroup?: number|false,
 *   mutate: Partial<Record<HistoryStoreName, (draft: any) => void>>
 * }} options
 * @returns {boolean} whether an entry was recorded
 */
export function commitHistory({ name, eventGroup, mutate }) {
    if (!mutate || typeof mutate !== "object") {
        throw new Error("commitHistory requires a mutate map");
    }

    const snapshots = [];
    for (const [storeName, recipe] of Object.entries(mutate)) {
        if (typeof recipe !== "function") {
            continue;
        }
        const name_ = /** @type {HistoryStoreName} */ (storeName);
        const [next] = produceWithPatches(getStorePlain(name_), recipe);
        setStorePlain(name_, plainClone(next));
        snapshots.push({ store: name_, value: plainClone(next) });
    }

    return insertHistoryAction({ name, eventGroup, snapshots });
}

/**
 * Record history after live stores were already mutated (e.g. Quill typing, drag).
 * Does not re-apply store state.
 * Pass `coalesceMs` + `coalesceKey` to merge rapid repeats of the same edit
 * (same name, group and key) into one undo step.
 * @param {{ name: string, eventGroup?: number|false, stores?: HistoryStoreName[], coalesceMs?: number, coalesceKey?: string|number }} options
 * @returns {boolean} whether an entry was recorded
 */
export function captureHistorySnapshot({
    name,
    eventGroup,
    stores = ["eventGroupState"],
    coalesceMs,
    coalesceKey,
}) {
    return insertHistoryAction(
        {
            name,
            eventGroup:
                eventGroup !== undefined ? eventGroup : projectState.selected,
            snapshots: stores.map((storeName) => ({
                store: storeName,
                value: getStorePlain(storeName),
            })),
        },
        { coalesceMs, coalesceKey },
    );
}

export function canUndo() {
    return historyState.position > 1;
}

export function canRedo() {
    return historyState.position < historyState.actions.length;
}

export function undoHistory() {
    flushPendingHistory();
    if (!canUndo()) {
        return;
    }

    const leaving = historyState.actions[historyState.position - 1];
    applyPatchesMap(leaving.inversePatches);
    historyState.position--;
    focusActionGroup(leaving);
}

export function redoHistory() {
    flushPendingHistory();
    if (!canRedo()) {
        return;
    }

    const current = historyState.actions[historyState.position];
    applyPatchesMap(current.patches);
    historyState.position++;
    focusActionGroup(current);
}
